import { Events } from '../events';
import { Scene } from '../scene';
import { pointerPositionInElement } from './pointer-coordinates';

export type VolugraphOrbitAnchor = { x: number; y: number; z: number };

/** Ignore small hand jitter so normal clicks still place the anchor. */
const CLICK_MOVE_THRESHOLD_PX = 8;

class OrbitAnchorTool {
    activate: () => void;
    deactivate: () => void;

    constructor(events: Events, scene: Scene, canvasContainer: { dom: HTMLElement }) {
        let active = false;
        let clickGate = false;
        let ptrDownX = 0;
        let ptrDownY = 0;
        let ptrMoved = false;
        let isEditMode = false;

        const isPrimary = (e: PointerEvent) => e.button === 0;

        const applyAnchor = (anchor: VolugraphOrbitAnchor) => {
            events.fire('volugraph.orbitAnchor.set', anchor);
            // Reframe only in view mode — during edit the merchant keeps current camera.
            if (!isEditMode) {
                events.fire('volugraph.orbitAnchor.frame');
            }
            scene.forceRender = true;
            try {
                const root = canvasContainer.dom.closest?.('[data-volugraph-embed]') ?? canvasContainer.dom;
                root?.dispatchEvent?.(
                    new CustomEvent('volugraph-orbit-anchor-set', {
                        bubbles: true,
                        detail: anchor,
                    }),
                );
            } catch {
                /* ignore */
            }
        };

        events.on('volugraph.editModeChange', (editMode: boolean) => {
            isEditMode = editMode;
        });

        const syncEditModeFromAttr = () => {
            const root = canvasContainer.dom.closest?.('[data-volugraph-embed]') ?? canvasContainer.dom;
            isEditMode = root.getAttribute?.('data-edit-mode') === 'true';
        };
        syncEditModeFromAttr();

        const pointerdown = (e: PointerEvent) => {
            if (!isPrimary(e)) return;
            clickGate = true;
            ptrDownX = e.clientX;
            ptrDownY = e.clientY;
            ptrMoved = false;
        };

        const pointermove = (e: PointerEvent) => {
            if (!clickGate) return;
            const dx = e.clientX - ptrDownX;
            const dy = e.clientY - ptrDownY;
            if (dx * dx + dy * dy > CLICK_MOVE_THRESHOLD_PX * CLICK_MOVE_THRESHOLD_PX) {
                ptrMoved = true;
            }
        };

        const pointerup = async (e: PointerEvent) => {
            if (!active || !clickGate || !isPrimary(e)) return;
            clickGate = false;
            if (ptrMoved) return;

            const target = e.target as HTMLElement;
            if (target?.closest?.('#left-annotation-toolbar')) return;
            if (target?.closest?.('#bottom-toolbar')) return;
            if (target?.closest?.('.annotation-numbered-marker')) return;
            if (target?.closest?.('.coord-marker-hit')) return;

            const ptr = pointerPositionInElement(e, scene.canvas);
            const result = await scene.camera.intersect(ptr.nx, ptr.ny);
            if (!result) return;

            const world = result.position;
            applyAnchor({ x: world.x, y: world.y, z: world.z });
            e.preventDefault();
            e.stopPropagation();
        };

        // Match RFI tool: listen on canvas-container; capture pointerup so it still fires after setPointerCapture.
        const pointerTarget = canvasContainer.dom;

        this.activate = () => {
            active = true;
            clickGate = false;
            ptrMoved = false;
            syncEditModeFromAttr();
            pointerTarget.addEventListener('pointerdown', pointerdown);
            pointerTarget.addEventListener('pointermove', pointermove);
            pointerTarget.addEventListener('pointerup', pointerup, true);
            canvasContainer.dom.classList.add('orbit-anchor-tool-active');
        };

        this.deactivate = () => {
            active = false;
            clickGate = false;
            ptrMoved = false;
            pointerTarget.removeEventListener('pointerdown', pointerdown);
            pointerTarget.removeEventListener('pointermove', pointermove);
            pointerTarget.removeEventListener('pointerup', pointerup, true);
            canvasContainer.dom.classList.remove('orbit-anchor-tool-active');
        };
    }
}

export { OrbitAnchorTool };
